import { useEffect, useState } from "react";
import {
    FiPlusCircle,
    FiList,
    FiEdit2,
    FiSave,
    FiX,
    FiAlertCircle,
    FiCalendar,
    FiClock,
    FiCheckCircle,
    FiAlertTriangle,
    FiInfo,
    FiPower
} from "react-icons/fi";
import {
    RiCalendarScheduleLine,
    RiCheckboxCircleFill
} from "react-icons/ri";
import { IoIosWarning } from "react-icons/io";
import axios from "axios";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/MaintenancePage.css";

const DEFAULT_MSG = "We add some features in site so maintenance will be going";

export default function MaintenancePage() {
    const [activeSubTab, setActiveSubTab] = useState("add");
    const [statusTab, setStatusTab] = useState("All");
    const [form, setForm] = useState({
        message: DEFAULT_MSG,
        startTime: "",
        endTime: "",
        status: "Scheduled"
    });
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({
        message: "",
        startTime: "",
        endTime: "",
        status: "Scheduled"
    });
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const [editSendMail, setEditSendMail] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    /* ---------------------------------------------
        IST <-> UTC helpers
    --------------------------------------------- */

    const convertUTCToISTInput = (utcString) => {
        if (!utcString) return "";

        const d = new Date(utcString);

        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: "Asia/Kolkata",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        }).formatToParts(d);

        const map = Object.fromEntries(
            parts.map((p) => [p.type, p.value])
        );

        return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
    };

    const convertISTInputToUTCISOString = (istInput) => {
        if (!istInput) return null;

        const [datePart, timePart] = istInput.split("T");
        const [y, m, d] = datePart.split("-").map(Number);
        const [hh, mm] = timePart.split(":").map(Number);

        const utcMillis =
            Date.UTC(y, m - 1, d, hh, mm) - 5.5 * 60 * 60 * 1000;

        return new Date(utcMillis).toISOString();
    };

    const formatIST = (utcString) => {
        if (!utcString) return "";

        return new Date(utcString).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        });
    };

    /* --------------------------------------------- */

    const validateDateTime = (data) => {
        if (!data.startTime || !data.endTime)
            return { isValid: false, message: "Please select both date & time" };

        const startUTC = new Date(
            convertISTInputToUTCISOString(data.startTime)
        );
        const endUTC = new Date(
            convertISTInputToUTCISOString(data.endTime)
        );

        if (startUTC >= endUTC)
            return {
                isValid: false,
                message: "End time must be after start time"
            };

        return { isValid: true };
    };

    const fetchMaintenance = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/maintenance`);
            setList(res.data.data || []);
        } catch (error) {
            toast.error("Failed to fetch maintenance records");
        }
    };

    useEffect(() => {
        if (activeSubTab === "manage") {
            fetchMaintenance();
        }
    }, [activeSubTab]);

    /* ---------------------------------------------
        Add
    --------------------------------------------- */

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const v = validateDateTime(form);
        if (!v.isValid) {
            toast.error(v.message);
            return;
        }

        try {
            setLoading(true);

            await axios.post(`${API_BASE_URL}/api/maintenance`, {
                message: form.message,
                startTime: convertISTInputToUTCISOString(form.startTime),
                endTime: convertISTInputToUTCISOString(form.endTime),
                status: form.status
            });

            toast.success("Maintenance scheduled successfully!");

            setForm({
                message: DEFAULT_MSG,
                startTime: "",
                endTime: "",
                status: "Scheduled"
            });

            // Switch to manage tab after successful add
            setActiveSubTab("manage");
        } catch (e) {
            toast.error("Failed to add maintenance");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------------------------------------
        Delete
    --------------------------------------------- */

    const handleRemove = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This maintenance schedule will be permanently removed!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#bfa56a',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: 'Yes, remove it!',
            cancelButtonText: 'Cancel',
            background: '#ffffff',
            color: '#1e293b',
            customClass: {
                popup: 'devopstrio-swal-popup'
            }
        });

        if (!result.isConfirmed) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/maintenance/${id}`);
            toast.success("Maintenance removed successfully!");
            fetchMaintenance();
        } catch (error) {
            toast.error("Failed to remove maintenance");
        }
    };

    /* ---------------------------------------------
        Edit / Update
    --------------------------------------------- */

    const startEdit = (row) => {
        setEditId(row._id);
        setEditSendMail(false);
        setEditForm({
            message: row.message,
            startTime: convertUTCToISTInput(row.startTime),
            endTime: convertUTCToISTInput(row.endTime),
            status: row.status
        });
    };

    const cancelEdit = () => {
        setEditId(null);
        toast.info("Edit cancelled");
    };

    const saveEdit = async () => {
        const v = validateDateTime(editForm);
        if (!v.isValid) {
            toast.error(v.message);
            return;
        }

        let toastId;

        try {
            setEditLoading(true);

            toastId = toast.loading(
                editSendMail
                    ? "Updating maintenance & sending mails..."
                    : "Updating maintenance..."
            );

            await axios.put(`${API_BASE_URL}/api/maintenance/${editId}`, {
                message: editForm.message,
                startTime: convertISTInputToUTCISOString(editForm.startTime),
                endTime: convertISTInputToUTCISOString(editForm.endTime),
                status: editForm.status,
                sendMail: editSendMail
            });

            toast.update(toastId, {
                render: editSendMail
                    ? "Maintenance updated and mails sent"
                    : "Maintenance updated successfully",
                type: "success",
                isLoading: false,
                autoClose: 3000
            });

            setEditId(null);
            fetchMaintenance();

        } catch (error) {

            if (toastId) {
                toast.update(toastId, {
                    render: "Failed to update maintenance",
                    type: "error",
                    isLoading: false,
                    autoClose: 3000
                });
            }

        } finally {
            setEditLoading(false);
        }
    };

    const getDisplayStatus = (item) => {

        // ✅ Ended tab must be ONLY status based
        if (item.status === "Ended") return "Ended";

        const now = new Date();
        const start = new Date(item.startTime);
        const end = new Date(item.endTime);

        // ✅ time based only for Scheduled & Active
        if (now < start) return "Scheduled";

        if (now >= start && now <= end) return "Active";

        // ❗ DO NOT auto mark Ended by time
        // keep it Active until backend marks it Ended
        return "Active";
    };

    /* ---------------------------------------------
        Filter
    --------------------------------------------- */

    const filteredList =
        statusTab === "All"
            ? list
            : list.filter(
                (i) => getDisplayStatus(i) === statusTab
            );


    const getStatusIcon = (status) => {
        switch (status) {
            case "Scheduled": return <RiCalendarScheduleLine className="status-icon scheduled" />;
            case "Active": return <IoIosWarning className="status-icon active" />;
            case "Ended": return <RiCheckboxCircleFill className="status-icon ended" />;
            default: return <FiInfo className="status-icon" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Scheduled": return "#3b82f6";
            case "Active": return "#ef4444";
            case "Ended": return "#10b981";
            default: return "#94a3b8";
        }
    };

    return (
        <div className="devopstrio-dashboard-wrapper">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            <div className="devopstrio-panel-header">
                <h2 className="devopstrio-panel-title">
                    <FiAlertCircle className="panel-title-icon" />
                    Maintenance Management
                </h2>
                <p className="panel-subtitle">
                    Schedule and manage system maintenance windows
                </p>
            </div>

            {/* Main sub tabs */}
            <div className="devopstrio-dashboard-tabs-card">
                <div
                    className={`devopstrio-tab-card ${activeSubTab === "add" ? "active" : ""}`}
                    onClick={() => setActiveSubTab("add")}
                >
                    <div className="tab-card-icon">
                        <FiPlusCircle />
                    </div>
                    <div className="tab-card-text">
                        <h4>Add New Maintenance</h4>
                        <p>Schedule a new maintenance window</p>
                    </div>
                    <div className="tab-card-line"></div>
                </div>

                <div
                    className={`devopstrio-tab-card ${activeSubTab === "manage" ? "active" : ""}`}
                    onClick={() => setActiveSubTab("manage")}
                >
                    <div className="tab-card-icon">
                        <FiList />
                    </div>
                    <div className="tab-card-text">
                        <h4>Manage Maintenance</h4>
                        <p>View and edit existing schedules</p>
                    </div>
                    <div className="tab-card-line"></div>
                </div>
            </div>

            {/* ---------------- Add ---------------- */}

            {activeSubTab === "add" && (
                <div className="devopstrio-dashboard-content">
                    <form
                        className="devopstrio-form"
                        onSubmit={handleSubmit}
                    >
                        <div className="form-group">
                            <label className="form-label">
                                <FiAlertCircle className="label-icon" />
                                Maintenance Message
                            </label>
                            <textarea
                                className="devopstrio-input"
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                rows={4}
                                placeholder="Enter maintenance message..."
                            />
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">
                                    <FiCalendar className="label-icon" />
                                    Start Time
                                </label>
                                <input
                                    className="devopstrio-input"
                                    type="datetime-local"
                                    name="startTime"
                                    value={form.startTime}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <FiClock className="label-icon" />
                                    End Time
                                </label>
                                <input
                                    className="devopstrio-input"
                                    type="datetime-local"
                                    name="endTime"
                                    value={form.endTime}
                                    min={form.startTime || undefined}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <RiCalendarScheduleLine className="label-icon" />
                                Initial Status
                            </label>
                            <select
                                className="devopstrio-input"
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                            >
                                <option value="Scheduled">Scheduled</option>
                                <option value="Active">Active</option>
                                <option value="Ended">Ended</option>
                            </select>
                        </div>

                        <button
                            type="submit"
                            className="devopstrio-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="loading-dots">
                                    <span></span><span></span><span></span>
                                </span>
                            ) : (
                                <>
                                    <FiSave className="btn-icon" />
                                    Schedule Maintenance
                                </>
                            )}
                        </button>
                    </form>
                </div>
            )}

            {/* ---------------- Manage ---------------- */}

            {activeSubTab === "manage" && (
                <div className="devopstrio-dashboard-content">
                    {/* Status tabs */}
                    <div className="devopstrio-status-tabs">
                        {["All", "Scheduled", "Active", "Ended"].map(
                            (s) => (
                                <button
                                    key={s}
                                    className={`devopstrio-status-tab ${statusTab === s ? "active" : ""}`}
                                    onClick={() => setStatusTab(s)}
                                    style={{
                                        backgroundColor: statusTab === s ? getStatusColor(s) : "transparent",
                                        color: statusTab === s ? "#ffffff" : "#64748b"
                                    }}
                                >
                                    {getStatusIcon(s)}
                                    <span>{s}</span>
                                    <span className="status-count">
                                        {s === "All" ? list.length : list.filter(i => getDisplayStatus(i) === s).length}
                                    </span>
                                </button>
                            )
                        )}
                    </div>

                    <div className="devopstrio-table-wrap">
                        {filteredList.length === 0 ? (
                            <div className="devopstrio-empty-state">
                                <div className="devopstrio-empty-state-icon">
                                    <FiAlertTriangle />
                                </div>
                                <h3>No maintenance records found</h3>
                                <p>
                                    {statusTab === "All"
                                        ? "No maintenance schedules have been created yet."
                                        : `No ${statusTab.toLowerCase()} maintenance schedules.`}
                                </p>
                            </div>
                        ) : (
                            <table className="devopstrio-table">
                                <thead>
                                    <tr>
                                        <th>Message</th>
                                        <th>Start</th>
                                        <th>End</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredList.map((item) => {
                                        const status = getDisplayStatus(item);
                                        const statusColor = getStatusColor(status);

                                        return (
                                            <tr key={item._id} className="devopstrio-table-row">
                                                <td className="message-cell">
                                                    {editId === item._id ? (
                                                        <textarea
                                                            className="devopstrio-input edit-textarea"
                                                            value={editForm.message}
                                                            onChange={(e) =>
                                                                setEditForm({
                                                                    ...editForm,
                                                                    message: e.target.value
                                                                })
                                                            }
                                                            rows={2}
                                                        />
                                                    ) : (
                                                        <div className="message-content">
                                                            <FiAlertCircle className="message-icon" />
                                                            <span>{item.message}</span>
                                                        </div>
                                                    )}
                                                </td>

                                                <td>
                                                    {editId === item._id ? (
                                                        <input
                                                            className="devopstrio-input edit-input"
                                                            type="datetime-local"
                                                            value={editForm.startTime}
                                                            onChange={(e) =>
                                                                setEditForm({
                                                                    ...editForm,
                                                                    startTime: e.target.value
                                                                })
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="time-cell">
                                                            <FiCalendar className="time-icon" />
                                                            {formatIST(item.startTime)}
                                                        </div>
                                                    )}
                                                </td>

                                                <td>
                                                    {editId === item._id ? (
                                                        <input
                                                            className="devopstrio-input edit-input"
                                                            type="datetime-local"
                                                            value={editForm.endTime}
                                                            onChange={(e) =>
                                                                setEditForm({
                                                                    ...editForm,
                                                                    endTime: e.target.value
                                                                })
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="time-cell">
                                                            <FiClock className="time-icon" />
                                                            {formatIST(item.endTime)}
                                                        </div>
                                                    )}
                                                </td>

                                                <td>
                                                    {editId === item._id ? (
                                                        <select
                                                            className="devopstrio-input edit-select"
                                                            value={editForm.status}
                                                            onChange={(e) =>
                                                                setEditForm({
                                                                    ...editForm,
                                                                    status: e.target.value
                                                                })
                                                            }
                                                        >
                                                            <option value="Scheduled">Scheduled</option>
                                                            <option value="Active">Active</option>
                                                            <option value="Ended">Ended</option>
                                                        </select>
                                                    ) : (
                                                        <div
                                                            className="status-badge"
                                                            style={{
                                                                backgroundColor: `${statusColor}15`,
                                                                color: statusColor
                                                            }}
                                                        >
                                                            {getStatusIcon(status)}
                                                            {status}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="devopstrio-action-col">
                                                    {editId === item._id ? (
                                                        <div
                                                            className="edit-actions"
                                                            style={{ display: "flex", flexDirection: "column", gap: 8 }}
                                                        >
                                                            {/* ✅ Optional mail checkbox */}
                                                            <label
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 6,
                                                                    fontSize: 12,
                                                                    color: "#475569",
                                                                    cursor: editLoading ? "not-allowed" : "pointer",
                                                                    opacity: editLoading ? 0.6 : 1
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={editSendMail}
                                                                    disabled={editLoading}
                                                                    onChange={(e) => setEditSendMail(e.target.checked)}
                                                                />
                                                                Send mail to users
                                                            </label>

                                                            <div style={{ display: "flex", gap: 8 }}>
                                                                <button
                                                                    onClick={saveEdit}
                                                                    className="devopstrio-action-btn save-btn"
                                                                    title="Save changes"
                                                                    disabled={editLoading}
                                                                >
                                                                    <FiSave />
                                                                </button>

                                                                <button
                                                                    onClick={cancelEdit}
                                                                    className="devopstrio-action-btn cancel-btn"
                                                                    title="Cancel"
                                                                    disabled={editLoading}
                                                                >
                                                                    <FiX />
                                                                </button>
                                                            </div>

                                                            {/* ✅ Loader shown outside buttons */}
                                                            {editLoading && (
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 6,
                                                                        fontSize: 12,
                                                                        color: "#64748b"
                                                                    }}
                                                                >
                                                                    <span className="loading-dots small">
                                                                        <span></span>
                                                                        <span></span>
                                                                        <span></span>
                                                                    </span>
                                                                    Updating maintenance...
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : getDisplayStatus(item) === "Ended" ? (
                                                        <div className="completed-status">
                                                            <FiCheckCircle /> Completed
                                                        </div>
                                                    ) : (
                                                        <div className="action-buttons">
                                                            <button
                                                                onClick={() => startEdit(item)}
                                                                className="devopstrio-action-btn edit-btn"
                                                                title="Edit maintenance"
                                                            >
                                                                <FiEdit2 />
                                                            </button>

                                                            <button
                                                                onClick={() => handleRemove(item._id)}
                                                                className="devopstrio-action-btn delete-btn"
                                                                title="Remove maintenance"
                                                            >
                                                                <FiPower />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>


                                            </tr>
                                        );
                                    })}
                                </tbody>

                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}